import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/useIsMobile";
import type { ApiTheme } from "@/features/themes/types";
import type { ApiDilemma } from "@/features/dilemmas/types";
import { catchError } from "@/lib/errors";
import { dilemmasRepo } from "@/lib/repositories";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import AiWritingAssistantInputView from "./ai-writing-assistant-input-view";
import AiWritingAssistantReviewView from "./ai-writing-assistant-review-view";
import { Sparkle } from "lucide-react";

interface AiWritingAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AiWritingAssistantDialog = ({
  open,
  onOpenChange,
}: AiWritingAssistantDialogProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const defaultLanguage: "fr" | "en" = i18n.language.startsWith("en")
    ? "en"
    : "fr";
  const [view, setView] = useState<"input" | "review">("input");
  const [selectedTheme, setSelectedTheme] = useState<ApiTheme | null>(null);
  const [idea, setIdea] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState<"fr" | "en">(
    defaultLanguage,
  );
  const [dilemma, setDilemma] = useState<ApiDilemma | null>(null);
  const [refineNonce, setRefineNonce] = useState(0);

  function resetState() {
    setView("input");
    setSelectedTheme(null);
    setIdea("");
    setSourceLanguage(defaultLanguage);
    setDilemma(null);
    setRefineNonce(0);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      // The generated draft exists server-side but wasn't published:
      // abandoning the flow deletes it.
      if (dilemma) {
        void catchError(dilemmasRepo.delete(dilemma.id)).then(([error]) => {
          if (error) toast.error(handleApiError(error).message);
        });
      }
      resetState();
    }
    onOpenChange(nextOpen);
  }

  function handleGenerated(newDilemma: ApiDilemma) {
    setDilemma(newDilemma);
    setView("review");
  }

  function handleRefined(newDilemma: ApiDilemma) {
    setDilemma(newDilemma);
    setRefineNonce((nonce) => nonce + 1);
  }

  function handlePublished() {
    // Published: reset without going through handleOpenChange so the draft
    // (now published) is not deleted.
    resetState();
    onOpenChange(false);
  }

  const title = t(
    view === "input"
      ? "modules.dilemmas.ai_assistant.modal_title"
      : "modules.dilemmas.ai_assistant.review_title",
  );

  const body =
    view === "input" ? (
      <AiWritingAssistantInputView
        selectedTheme={selectedTheme}
        setSelectedTheme={setSelectedTheme}
        idea={idea}
        setIdea={setIdea}
        sourceLanguage={sourceLanguage}
        setSourceLanguage={setSourceLanguage}
        onCancel={() => handleOpenChange(false)}
        onGenerated={handleGenerated}
      />
    ) : dilemma ? (
      <AiWritingAssistantReviewView
        key={`${dilemma.id}-${refineNonce}`}
        dilemma={dilemma}
        onRefined={handleRefined}
        onPublished={handlePublished}
        onCancel={() => handleOpenChange(false)}
      />
    ) : null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription className="sr-only">{title}</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-4">{body}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkle className="text-primary" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
};

export default AiWritingAssistantDialog;
